import React, { useState, useCallback } from 'react';
import {
  ClockIcon,
  CalendarDaysIcon,
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  ChatBubbleLeftRightIcon,
  BellIcon,
  ShieldCheckIcon,
  Cog6ToothIcon,
  CheckIcon,
  XMarkIcon,
  VideoCameraIcon,
  MapPinIcon,
  GlobeAltIcon
} from '@heroicons/react/24/outline';
import { BookingSettings } from '../../types/appointments';
import { appointmentService } from '../../services/appointmentService';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../common/Button';
import GoogleAuthButton from './GoogleAuthButton';

interface BookingSettingsFormProps {
  settings: BookingSettings | null;
  onSave: (settings: BookingSettings) => void;
  onCancel: () => void;
}

const BookingSettingsForm: React.FC<BookingSettingsFormProps> = ({
  settings,
  onSave,
  onCancel
}) => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'basic' | 'schedule' | 'collection' | 'meeting' | 'notifications'>('basic');
  const [formData, setFormData] = useState<Partial<BookingSettings>>({
    title: settings?.title || '',
    description: settings?.description || '',
    duration: settings?.duration || 60,
    availableHours: settings?.availableHours || {
      start: '09:00',
      end: '17:00',
      days: [1, 2, 3, 4, 5] // Hétfő-Péntek
    },
    bufferTime: settings?.bufferTime || 15,
    advanceBooking: settings?.advanceBooking || {
      min: 2, // 2 óra
      max: 30 // 30 nap
    },
    requireConfirmation: settings?.requireConfirmation || false,
    collectInfo: settings?.collectInfo || {
      name: true,
      email: true,
      phone: true,
      message: false
    },
    notifications: settings?.notifications || {
      email: true,
      sms: false,
      reminderHours: [24, 2]
    },
    meetingOptions: settings?.meetingOptions || {
      enableOnlineMeeting: true,
      defaultMeetingType: 'hybrid',
      allowClientChoice: true,
      autoGenerateMeetLink: false,
      meetingInstructions: '',
      selectedCalendarId: 'primary',
      selectedCalendarName: 'Elsődleges naptár'
    },
    isActive: settings?.isActive ?? true,
    organizationId: user?.organizationId || '',
    createdBy: user?.uid || ''
  });

  const [isSaving, setIsSaving] = useState(false);

  const updateFormData = useCallback((field: keyof BookingSettings, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  const updateNestedField = useCallback((parent: keyof BookingSettings, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [parent]: {
        ...(prev[parent] as any),
        [field]: value
      }
    }));
  }, []);

  const handleSave = async () => {
    if (!formData.title || !user?.organizationId) return;

    setIsSaving(true);
    try {
      if (settings?.id) {
        // Frissítés
        await appointmentService.updateBookingSettings(settings.id, formData);
        onSave({ ...settings, ...formData } as BookingSettings);
      } else {
        // Új létrehozás
        const id = await appointmentService.createBookingSettings({
          ...formData,
          organizationId: user.organizationId,
          createdBy: user.uid
        } as Omit<BookingSettings, 'id' | 'createdAt' | 'updatedAt'>);
        onSave({ ...formData, id } as BookingSettings);
      }
    } catch (error) {
      console.error('Error saving booking settings:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const tabs = [
    { id: 'basic', name: 'Alapadatok', icon: Cog6ToothIcon },
    { id: 'schedule', name: 'Ütemezés', icon: CalendarDaysIcon },
    { id: 'collection', name: 'Adatgyűjtés', icon: UserIcon },
    { id: 'meeting', name: 'Meeting Típus', icon: VideoCameraIcon },
    { id: 'notifications', name: 'Értesítések', icon: BellIcon },
  ] as const;

  const weekDays = [
    { id: 1, name: 'Hétfő', short: 'H' },
    { id: 2, name: 'Kedd', short: 'K' },
    { id: 3, name: 'Szerda', short: 'Sze' },
    { id: 4, name: 'Csütörtök', short: 'Cs' },
    { id: 5, name: 'Péntek', short: 'P' },
    { id: 6, name: 'Szombat', short: 'Szo' },
    { id: 0, name: 'Vasárnap', short: 'V' },
  ];

  return (
    <div className="h-full flex flex-col">
      {/* Fejléc gradientel */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4">
        <h2 className="text-xl font-semibold text-white">
          {settings ? 'Beállítás szerkesztése' : 'Új foglalási beállítás'}
        </h2>
        <p className="text-indigo-100 text-sm mt-1">
          Konfiguráld a foglalási szolgáltatásod részleteit
        </p>
      </div>

      {/* Tab navigáció */}
      <div className="border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <nav className="flex space-x-8 px-6" aria-label="Tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition-colors
                ${activeTab === tab.id
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }
              `}
            >
              <tab.icon className="h-4 w-4" />
              <span>{tab.name}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Tartalom */}
      <div className="flex-1 overflow-y-auto p-6 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-2xl mx-auto">
          {/* Alapadatok tab */}
          {activeTab === 'basic' && (
            <div className="space-y-6">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="p-2 bg-indigo-100 dark:bg-indigo-900/20 rounded-lg">
                    <Cog6ToothIcon className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                    Szolgáltatás Információk
                  </h3>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Szolgáltatás neve *
                    </label>
                    <input
                      type="text"
                      value={formData.title || ''}
                      onChange={(e) => updateFormData('title', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                      placeholder="pl. Konzultáció, Bemutató, Tervezési megbeszélés"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Leírás
                    </label>
                    <textarea
                      value={formData.description || ''}
                      onChange={(e) => updateFormData('description', e.target.value)}
                      rows={3}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors resize-none"
                      placeholder="Rövid leírás a szolgáltatásról, amit az ügyfelek látni fognak..."
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Időtartam (perc) *
                      </label>
                      <div className="relative">
                        <ClockIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                        <input
                          type="number"
                          value={formData.duration || ''}
                          onChange={(e) => updateFormData('duration', parseInt(e.target.value) || 60)}
                          className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                          min="15"
                          max="480"
                          placeholder="60"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Szünet időpontok között (perc)
                      </label>
                      <input
                        type="number"
                        value={formData.bufferTime || ''}
                        onChange={(e) => updateFormData('bufferTime', parseInt(e.target.value) || 0)}
                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                        min="0"
                        max="60"
                        placeholder="15"
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
                    <div className="flex items-center space-x-3">
                      <ShieldCheckIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          Manuális jóváhagyás szükséges
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          A foglalások csak jóváhagyás után lesznek véglegesek
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => updateFormData('requireConfirmation', !formData.requireConfirmation)}
                      className={`
                        relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
                        ${formData.requireConfirmation ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-600'}
                      `}
                    >
                      <span
                        className={`
                          inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                          ${formData.requireConfirmation ? 'translate-x-6' : 'translate-x-1'}
                        `}
                      />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Ütemezés tab */}
          {activeTab === 'schedule' && (
            <div className="space-y-6">
              {/* Munkaidő */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
                    <ClockIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                    Munkaidő
                  </h3>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Nap kezdete
                    </label>
                    <input
                      type="time"
                      value={formData.availableHours?.start || '09:00'}
                      onChange={(e) => updateNestedField('availableHours', 'start', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Nap vége
                    </label>
                    <input
                      type="time"
                      value={formData.availableHours?.end || '17:00'}
                      onChange={(e) => updateNestedField('availableHours', 'end', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    Elérhető napok
                  </label>
                  <div className="grid grid-cols-7 gap-2">
                    {weekDays.map((day) => (
                      <button
                        key={day.id}
                        onClick={() => {
                          const currentDays = formData.availableHours?.days || [];
                          const newDays = currentDays.includes(day.id)
                            ? currentDays.filter(d => d !== day.id)
                            : [...currentDays, day.id];
                          updateNestedField('availableHours', 'days', newDays);
                        }}
                        className={`
                          p-3 rounded-lg border text-sm font-medium transition-all
                          ${formData.availableHours?.days?.includes(day.id)
                            ? 'bg-primary-600 text-white border-primary-600 shadow-md'
                            : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:border-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/20'
                          }
                        `}
                      >
                        <div className="text-center">
                          <div className="font-semibold">{day.short}</div>
                          <div className="text-xs opacity-75">{day.name}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Előrejelzési beállítások */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="p-2 bg-green-100 dark:bg-green-900/20 rounded-lg">
                    <CalendarDaysIcon className="h-5 w-5 text-green-600 dark:text-green-400" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                    Foglalási Korlátok
                  </h3>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Minimum előrejelzés (óra)
                    </label>
                    <input
                      type="number"
                      value={formData.advanceBooking?.min || ''}
                      onChange={(e) => updateNestedField('advanceBooking', 'min', parseInt(e.target.value) || 2)}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                      min="0"
                      max="168"
                      placeholder="2"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Hány órával előre lehet legkorábban foglalni
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Maximum előrejelzés (nap)
                    </label>
                    <input
                      type="number"
                      value={formData.advanceBooking?.max || ''}
                      onChange={(e) => updateNestedField('advanceBooking', 'max', parseInt(e.target.value) || 30)}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                      min="1"
                      max="365"
                      placeholder="30"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Hány nappal előre lehet legkésőbb foglalni
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Adatgyűjtés tab */}
          {activeTab === 'collection' && (
            <div className="space-y-6">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="p-2 bg-purple-100 dark:bg-purple-900/20 rounded-lg">
                    <UserIcon className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                    Ügyfél Adatok Gyűjtése
                  </h3>
                </div>

                <div className="space-y-4">
                  {[
                    { key: 'name', label: 'Név', icon: UserIcon, required: true },
                    { key: 'email', label: 'Email cím', icon: EnvelopeIcon, required: true },
                    { key: 'phone', label: 'Telefonszám', icon: PhoneIcon, required: false },
                    { key: 'message', label: 'Üzenet/Megjegyzés', icon: ChatBubbleLeftRightIcon, required: false },
                  ].map((field) => (
                    <div key={field.key} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
                      <div className="flex items-center space-x-3">
                        <field.icon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {field.label}
                            {field.required && <span className="text-red-500 ml-1">*</span>}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {field.required ? 'Kötelező mező' : 'Opcionális mező'}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => updateNestedField('collectInfo', field.key, !formData.collectInfo?.[field.key as keyof typeof formData.collectInfo])}
                        disabled={field.required}
                        className={`
                          relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
                          ${field.required ? 'bg-gray-300 cursor-not-allowed' : ''}
                          ${formData.collectInfo?.[field.key as keyof typeof formData.collectInfo] ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-600'}
                        `}
                      >
                        <span
                          className={`
                            inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                            ${formData.collectInfo?.[field.key as keyof typeof formData.collectInfo] ? 'translate-x-6' : 'translate-x-1'}
                          `}
                        />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Meeting Típus tab */}
          {activeTab === 'meeting' && (
            <div className="space-y-6">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
                    <VideoCameraIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                    Meeting Beállítások
                  </h3>
                </div>

                <div className="space-y-6">
                  {/* Online meeting engedélyezése */}
                  <div className="flex items-center justify-between">
                    <div>
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Online meeting engedélyezése
                      </label>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Lehetővé teszi az online találkozókat
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => updateNestedField('meetingOptions', 'enableOnlineMeeting', !formData.meetingOptions?.enableOnlineMeeting)}
                      className={`
                        relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent 
                        transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
                        ${formData.meetingOptions?.enableOnlineMeeting ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-700'}
                      `}
                    >
                      <span
                        className={`
                          pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 
                          transition duration-200 ease-in-out
                          ${formData.meetingOptions?.enableOnlineMeeting ? 'translate-x-5' : 'translate-x-0'}
                        `}
                      />
                    </button>
                  </div>

                  {/* Alapértelmezett meeting típus */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Alapértelmezett meeting típus
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      {/* Személyes */}
                      <button
                        type="button"
                        onClick={() => updateNestedField('meetingOptions', 'defaultMeetingType', 'in_person')}
                        className={`
                          relative flex items-center space-x-3 p-4 border rounded-lg transition-all duration-200
                          ${formData.meetingOptions?.defaultMeetingType === 'in_person'
                            ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                            : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 bg-white dark:bg-gray-800'
                          }
                        `}
                      >
                        <MapPinIcon className={`
                          h-5 w-5 
                          ${formData.meetingOptions?.defaultMeetingType === 'in_person'
                            ? 'text-green-600 dark:text-green-400'
                            : 'text-gray-400'
                          }
                        `} />
                        <div className="text-left">
                          <div className={`
                            text-sm font-medium
                            ${formData.meetingOptions?.defaultMeetingType === 'in_person'
                              ? 'text-green-900 dark:text-green-100'
                              : 'text-gray-900 dark:text-gray-100'
                            }
                          `}>
                            Személyes
                          </div>
                        </div>
                        {formData.meetingOptions?.defaultMeetingType === 'in_person' && (
                          <CheckIcon className="h-4 w-4 text-green-600 dark:text-green-400 absolute top-2 right-2" />
                        )}
                      </button>

                      {/* Online */}
                      <button
                        type="button"
                        onClick={() => updateNestedField('meetingOptions', 'defaultMeetingType', 'online')}
                        className={`
                          relative flex items-center space-x-3 p-4 border rounded-lg transition-all duration-200
                          ${formData.meetingOptions?.defaultMeetingType === 'online'
                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                            : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 bg-white dark:bg-gray-800'
                          }
                        `}
                      >
                        <VideoCameraIcon className={`
                          h-5 w-5 
                          ${formData.meetingOptions?.defaultMeetingType === 'online'
                            ? 'text-blue-600 dark:text-blue-400'
                            : 'text-gray-400'
                          }
                        `} />
                        <div className="text-left">
                          <div className={`
                            text-sm font-medium
                            ${formData.meetingOptions?.defaultMeetingType === 'online'
                              ? 'text-blue-900 dark:text-blue-100'
                              : 'text-gray-900 dark:text-gray-100'
                            }
                          `}>
                            Online
                          </div>
                        </div>
                        {formData.meetingOptions?.defaultMeetingType === 'online' && (
                          <CheckIcon className="h-4 w-4 text-blue-600 dark:text-blue-400 absolute top-2 right-2" />
                        )}
                      </button>

                      {/* Hibrid */}
                      <button
                        type="button"
                        onClick={() => updateNestedField('meetingOptions', 'defaultMeetingType', 'hybrid')}
                        className={`
                          relative flex items-center space-x-3 p-4 border rounded-lg transition-all duration-200
                          ${formData.meetingOptions?.defaultMeetingType === 'hybrid'
                            ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                            : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 bg-white dark:bg-gray-800'
                          }
                        `}
                      >
                        <GlobeAltIcon className={`
                          h-5 w-5 
                          ${formData.meetingOptions?.defaultMeetingType === 'hybrid'
                            ? 'text-purple-600 dark:text-purple-400'
                            : 'text-gray-400'
                          }
                        `} />
                        <div className="text-left">
                          <div className={`
                            text-sm font-medium
                            ${formData.meetingOptions?.defaultMeetingType === 'hybrid'
                              ? 'text-purple-900 dark:text-purple-100'
                              : 'text-gray-900 dark:text-gray-100'
                            }
                          `}>
                            Hibrid
                          </div>
                        </div>
                        {formData.meetingOptions?.defaultMeetingType === 'hybrid' && (
                          <CheckIcon className="h-4 w-4 text-purple-600 dark:text-purple-400 absolute top-2 right-2" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Kliens választhat */}
                  <div className="flex items-center justify-between">
                    <div>
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Kliens választhat meeting típust
                      </label>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        A kliens dönthet személyes vagy online találkozó között
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => updateNestedField('meetingOptions', 'allowClientChoice', !formData.meetingOptions?.allowClientChoice)}
                      className={`
                        relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent 
                        transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
                        ${formData.meetingOptions?.allowClientChoice ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-700'}
                      `}
                    >
                      <span
                        className={`
                          pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 
                          transition duration-200 ease-in-out
                          ${formData.meetingOptions?.allowClientChoice ? 'translate-x-5' : 'translate-x-0'}
                        `}
                      />
                    </button>
                  </div>

                  {/* Google Calendar integráció */}
                  {formData.meetingOptions?.enableOnlineMeeting && (
                    <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                      <div className="mb-4">
                        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Google Calendar Integráció
                        </h4>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Csatlakoztasd a Google Calendar-t az automatikus Meet link generáláshoz
                        </p>
                      </div>
                      
                      <GoogleAuthButton 
                        selectedCalendarId={formData.meetingOptions.selectedCalendarId}
                        onCalendarSelect={(calendarId, calendarName) => {
                          updateNestedField('meetingOptions', 'selectedCalendarId', calendarId);
                          updateNestedField('meetingOptions', 'selectedCalendarName', calendarName);
                        }}
                        onAuthChange={(isAuthenticated) => {
                          updateNestedField('meetingOptions', 'autoGenerateMeetLink', isAuthenticated);
                        }}
                      />
                    </div>
                  )}

                  {/* Meeting instrukciók */}
                  {formData.meetingOptions?.enableOnlineMeeting && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Meeting instrukciók
                      </label>
                      <textarea
                        value={formData.meetingOptions?.meetingInstructions || ''}
                        onChange={(e) => updateNestedField('meetingOptions', 'meetingInstructions', e.target.value)}
                        rows={3}
                        className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                        placeholder="Pl.: A meeting link automatikusan elküldésre kerül az email megerősítésben..."
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Ezek az instrukciók megjelennek a kliens számára a foglalás megerősítésében
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Értesítések tab */}
          {activeTab === 'notifications' && (
            <div className="space-y-6">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="p-2 bg-yellow-100 dark:bg-yellow-900/20 rounded-lg">
                    <BellIcon className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                    Értesítési Beállítások
                  </h3>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
                    <div className="flex items-center space-x-3">
                      <EnvelopeIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          Email értesítések
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Visszaigazolás és emlékeztetők emailben
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => updateNestedField('notifications', 'email', !formData.notifications?.email)}
                      className={`
                        relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
                        ${formData.notifications?.email ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-600'}
                      `}
                    >
                      <span
                        className={`
                          inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                          ${formData.notifications?.email ? 'translate-x-6' : 'translate-x-1'}
                        `}
                      />
                    </button>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
                    <div className="flex items-center space-x-3">
                      <PhoneIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          SMS értesítések
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Emlékeztetők SMS-ben (hamarosan)
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => updateNestedField('notifications', 'sms', !formData.notifications?.sms)}
                      disabled={true} // Egyelőre letiltva
                      className="relative inline-flex h-6 w-11 items-center rounded-full bg-gray-200 cursor-not-allowed"
                    >
                      <span className="inline-block h-4 w-4 transform rounded-full bg-white translate-x-1" />
                    </button>
                  </div>

                  {formData.notifications?.email && (
                    <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                      <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">
                        Emlékeztető időpontok (órában az időpont előtt)
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {[1, 2, 4, 8, 24, 48, 72].map((hours) => (
                          <button
                            key={hours}
                            onClick={() => {
                              const currentReminders = formData.notifications?.reminderHours || [];
                              const newReminders = currentReminders.includes(hours)
                                ? currentReminders.filter(h => h !== hours)
                                : [...currentReminders, hours].sort((a, b) => b - a);
                              updateNestedField('notifications', 'reminderHours', newReminders);
                            }}
                            className={`
                              px-3 py-2 rounded-lg text-sm font-medium transition-all
                              ${formData.notifications?.reminderHours?.includes(hours)
                                ? 'bg-primary-600 text-white shadow-md'
                                : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:border-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/20'
                              }
                            `}
                          >
                            {hours}h
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Alsó akció sáv */}
      <div className="border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-2">
              <div className={`
                w-3 h-3 rounded-full
                ${formData.isActive ? 'bg-green-500' : 'bg-gray-400'}
              `} />
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {formData.isActive ? 'Aktív' : 'Inaktív'}
              </span>
            </div>
            <button
              onClick={() => updateFormData('isActive', !formData.isActive)}
              className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 font-medium"
            >
              {formData.isActive ? 'Deaktiválás' : 'Aktiválás'}
            </button>
          </div>

          <div className="flex items-center space-x-3">
            <Button
              variant="outline"
              onClick={onCancel}
              disabled={isSaving}
              className="flex items-center space-x-2"
            >
              <XMarkIcon className="h-4 w-4" />
              <span>Mégse</span>
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={isSaving || !formData.title}
              className="flex items-center space-x-2"
            >
              {isSaving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  <span>Mentés...</span>
                </>
              ) : (
                <>
                  <CheckIcon className="h-4 w-4" />
                  <span>{settings ? 'Frissítés' : 'Létrehozás'}</span>
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingSettingsForm;
